// 1 screen of Start here (live's Step): the row's eyebrow ("Section 1, Deal analysis"), the title,
// the 1-line takeaway, the formula where the screen has it, the words, the diagrams, the screen's
// widget, its quick check and assignment, then Back and the button on: "Done, next screen" (live's),
// held back with live's line beside it until everything on the screen is answered, "Next screen"
// once it's done. The gate, the last screen, is done by its 3 ticks; it shows Back, and Finish once
// it's done, back to the course's home.
//
// Answers save as they go (data.js saveScreen), 1 at a time and in order, merged on screen at once.
// The database's answer replaces them, so its scores, its "right" and its second channel's label are
// what shows. A save it refuses, or one that doesn't arrive, puts the screen back as it was saved and
// says why under the buttons. While staff view as a member nothing saves (readOnly).
import { useCallback, useRef, useState } from 'react'
import { Link, navigate } from '../../../lib/router.jsx'
import { readOnlyControl, VIEWING_AS_LINE } from '../../../lib/viewAs.jsx'
import { Tick } from '../../../product/Icons.jsx'
import { COPY } from './copy.js'
import { Md } from './Md.jsx'
import { Visuals } from './Visuals.jsx'
import { Assignment, BudgetMapper, CardJudge, Calculator, ChannelChoice, Channels, Drill, Gate, Quiz, Walkthrough } from './Widgets.jsx'
import { saveScreen } from './data.js'
import { missingOn, refusalKind, screenHref, withProgress } from './model.js'

export function lineFor(error, viewingAs) {
  const kind = refusalKind(error, { viewingAs })
  if (kind === 'viewing_as') return VIEWING_AS_LINE
  if (kind === 'connection') return COPY.saveFailed
  return COPY.refused[kind] || COPY.refused.fallback
}

function gateHelp(kind, cfg) {
  const h = COPY.gateHelp[kind]
  if (kind === 'drill') return h((cfg.houses || []).length || 3)
  if (kind === 'channels') return h((cfg.channels || []).length || 7)
  return typeof h === 'string' ? h : ''
}

export function Formula() {
  return (
    <div className="ps-formula">
      <p className="ps-formula-line">{COPY.formulaLead}<span className="ps-formula-hl">{COPY.formulaPct}</span>{COPY.formulaRest}</p>
      <p className="ps-formula-note">{COPY.formulaNote}</p>
    </div>
  )
}

export function Eyebrow({ row }) {
  return <div className="ps-eyebrow">{COPY.eyebrow(row.config?.label, row.title)}</div>
}

export function Screen({ product, model, entry, personId, viewingAs, patch }) {
  const { screen, row, pos } = entry
  const cfg = screen.config && typeof screen.config === 'object' ? screen.config : {}
  const saved = model.answersOf(screen)
  const done = model.isDone(screen)
  const [overlay, setOverlay] = useState(null)
  const [line, setLine] = useState(null)
  const [busy, setBusy] = useState(false)
  const queue = useRef(Promise.resolve())
  const seq = useRef(0)
  const answers = overlay ? { ...saved, ...overlay } : saved

  const save = useCallback((delta, { done: markDone = false } = {}) => {
    if (viewingAs) {
      setLine(VIEWING_AS_LINE)
      return Promise.reject(new Error('viewing_as'))
    }
    const n = ++seq.current
    setLine(null)
    setOverlay(o => ({ ...(o || {}), ...delta }))
    const run = queue.current.then(() => saveScreen(personId, screen.id, delta, { done: markDone }))
    queue.current = run.catch(() => {})
    return run.then(rowBack => {
      if (rowBack) patch(d => withProgress(d, rowBack))
      if (n === seq.current) setOverlay(null)
      return rowBack
    }, e => {
      if (n === seq.current) setOverlay(null)
      setLine(lineFor(e, viewingAs))
      throw e
    })
  }, [viewingAs, personId, screen.id, patch])

  const prev = model.order[pos - 2] || null
  const next = model.order[pos] || null
  const isGate = cfg.type === 'gate'
  const missing = done ? [] : missingOn(cfg, answers).filter(k => k !== 'gate')
  const waiting = missing.length > 0
  const choiceScreen = model.order.find(o => o.screen.config?.type === 'channel_choice')?.screen || null
  const choice = choiceScreen ? model.answersOf(choiceScreen) : {}

  async function onDone() {
    if (busy) return
    if (done) {
      if (next) navigate(screenHref(product, next.row, next.screen))
      return
    }
    if (viewingAs || waiting) return
    setBusy(true)
    try {
      await save({}, { done: true })
      if (next) navigate(screenHref(product, next.row, next.screen))
    } catch { /* the line says why */ } finally {
      setBusy(false)
    }
  }

  const common = { cfg, answers, save, readOnly: viewingAs }
  let widget = null
  switch (cfg.type) {
    case 'walkthrough': widget = <Walkthrough {...common} />; break
    case 'comp_filter': widget = <CardJudge {...common} mode="binary" />; break
    case 'sorter': widget = <CardJudge {...common} mode="triple" />; break
    case 'calculator': widget = <Calculator {...common} />; break
    case 'drill': widget = <Drill {...common} />; break
    case 'channels': widget = <Channels {...common} />; break
    case 'channel_choice': widget = <ChannelChoice {...common} />; break
    case 'budget_mapper': widget = <BudgetMapper {...common} chosen={typeof choice.secondary === 'string' ? choice.secondary : null} />; break
    case 'gate': widget = <Gate {...common} secondLabel={typeof choice.secondary_label === 'string' ? choice.secondary_label : null} />; break
    default: widget = null
  }

  return (
    <article className="ps-card" aria-labelledby="ps-h1" data-screen={screen.step_key}>
      <Eyebrow row={row} />
      <h1 className="ps-h1" id="ps-h1">{screen.title}</h1>
      {cfg.takeaway && <div className="ps-takeaway"><b>{COPY.takeaway}</b><p>{cfg.takeaway}</p></div>}
      {cfg.formula_before && <Formula />}
      <Md text={screen.body_md} />
      {cfg.formula_after && <Formula />}
      <Visuals spec={cfg.visual} />
      {widget}
      {cfg.quiz && <Quiz quiz={cfg.quiz} answers={answers} save={save} readOnly={viewingAs} />}
      {cfg.assignment && !isGate && <Assignment cfg={cfg.assignment} answers={answers} save={save} readOnly={viewingAs} />}
      {cfg.outro_md && <Md text={cfg.outro_md} className="ps-outro" />}
      <div className="ps-foot">
        <div className="ps-foot-left">
          {prev && <Link className="ps-btn ps-btn--quiet" to={screenHref(product, prev.row, prev.screen)}>{COPY.back}</Link>}
        </div>
        <div className="ps-foot-right">
          {!isGate && waiting && <span className="ps-gate-help">{gateHelp(missing[0], cfg)}</span>}
          {done && <span className="ps-done-tag"><Tick size={14} stroke={3} />{COPY.doneTag}</span>}
          {isGate
            ? (done && <Link className="ps-btn" to={product.path || '/'}>{COPY.finish}</Link>)
            : (
              <button type="button" className="ps-btn" onClick={onDone} aria-busy={busy || undefined}
                disabled={busy || (!done && (waiting || viewingAs))} {...(!done ? readOnlyControl(viewingAs) : {})}>
                {done ? COPY.nextScreen : COPY.doneNextScreen}
              </button>
            )}
        </div>
      </div>
      {line && <p className="ps-line" role="alert">{line}</p>}
    </article>
  )
}

// A screen that isn't open yet: its name, the database's line, and the way to where they're up to
export function LockedScreen({ product, model, entry }) {
  const to = model.resume
  return (
    <article className="ps-card ps-card--locked" aria-labelledby="ps-h1" data-screen={entry.screen.step_key}>
      <Eyebrow row={entry.row} />
      <h1 className="ps-h1" id="ps-h1">{entry.screen.title}</h1>
      <p className="ps-locked-line">{COPY.locked}</p>
      {to && <Link className="ps-btn ps-locked-go" to={screenHref(product, to.row, to.screen)}>{product.settings?.start_here?.buttons?.continue || 'Continue'}</Link>}
    </article>
  )
}
